/**
 * What a document holds, and the two presses that ask for it.
 *
 * The material arrives in two halves, and this is both of them: the *page table* —
 * what each page holds, and the deck's totals — is known from the post (or from the
 * composer's own state) without asking anyone, so every badge and every control can
 * be drawn on the first paint; the *content* — the clips' addresses and the questions
 * themselves — is fetched, and only once somebody actually plays. `wantsMaterial` is
 * that moment, and until it flips this card has made no request for material at all.
 *
 * It is a hook rather than part of `PdfCarousel` because this is the part of that
 * surface with the longest memory: an address recovered from a re-read post, a post
 * being read right now, a fetch that cannot be enabled until that read answers, and a
 * fetch that has to be told apart from one that answered *nothing*. A change to how
 * material is asked for should be readable without the stage's JSX around it — and a
 * change to the stage's JSX should not have to be read against this
 * (`IMPROVEMENT_BACKLOG.md` §3, the rest of the two surfaces' split).
 *
 * What it does not own: whether the card is the author's (`isComposing` is an input,
 * because the composer's material is the sheet's own state and is never fetched), the
 * page the reader is on, and anything the plays of the document have to say — a play's
 * board and this reader's resume are read from what people *did* with the material
 * (`./useDocumentPlaySession`).
 */
import {useState} from 'react'
import {type AtpAgent} from '@atproto/api'

import {
  type DocumentMaterial,
  type DocumentMaterialRef,
  type DocumentMaterialState,
  documentMaterialState,
  firstQuestionPage,
  materialPagesOf,
  totalsOf,
} from '#/lib/document/material'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {
  deriveDocumentMaterialRef,
  useDocumentMaterialQuery,
} from '#/state/queries/document-material'

export function useStageMaterial({
  materialRef,
  pageMaterial,
  isComposing,
  postUri,
  agent,
}: {
  /**
   * Where the post says the material is, and what each page holds.
   *
   * The caller's copy of the post's own record — `materialRefFromRecord` off a record
   * the app cached — so it is right until the post is re-read.
   */
  materialRef?: DocumentMaterialRef
  /** The composer's own material: the author has authored it, so there is nothing to fetch. */
  pageMaterial?: DocumentMaterial
  /** Whether this card is the author writing the material rather than a reader playing it. */
  isComposing: boolean
  /** The post this document belongs to, once it has one. */
  postUri?: string
  /** The agent a re-read of the post is made with. */
  agent: AtpAgent
}) {
  /*
   * The material, in two halves.
   *
   *  • The *page table* — what each page holds, and the deck's totals — is known
   *    from the post (or from the composer's own state) without asking anyone, so
   *    every badge and every control can be drawn on the first paint.
   *  • The *content* — the clips' addresses and the questions themselves — is
   *    fetched, and only once somebody actually plays. `wantsMaterial` is that
   *    moment; until it flips, this reader has made no request for material at all.
   */
  const [wantsMaterial, setWantsMaterial] = useState(false)
  /*
   * The address this card asks at, and the one a recovery found.
   *
   * The prop is the post's own copy — `materialRefFromRecord` off a record the app
   * cached — and it is right until the post is re-read. A recovery does exactly that
   * (`deriveDocumentMaterialRef`), and its answer is held here rather than written
   * anywhere: the prop is still what the caller knows, and *this* is what the post's
   * record says. One thing makes the held one drop back out, and it is not an effect: a
   * prop that names a different address than the one recovery started from wins, because
   * a caller re-reading the post is more current than a press was. Nothing else has to
   * happen for it — the held ref is read only while the prop still stands where it did.
   */
  const [recovered, setRecovered] = useState<{
    namedBy?: string
    ref: DocumentMaterialRef
  }>()
  const materialRefNow =
    recovered && recovered.namedBy === materialRef?.uri
      ? recovered.ref
      : materialRef
  /*
   * Whether the post's own record is being read, which is the round trip that has to land
   * before the material can be asked for at all (`reReadThePost` below). Held as state rather
   * than as a ref, because two things draw themselves differently while it is true: the ask
   * waits for it (the query is not enabled until it lands), and the read it is waiting on is
   * reported as *coming* below rather than as nothing there.
   */
  const [isReadingPost, setIsReadingPost] = useState(false)
  const materialQuery = useDocumentMaterialQuery(materialRefNow?.uri, {
    /*
     * The ask is held while the post is read, which is the fix for the document that reads as
     * empty: the address this card holds is the app's *cached* copy of the post's record, and a
     * read made at it is answered by whatever holds nothing there. What is *not* held is the
     * press's own effect on the card: `wantsMaterial` flips on the press (below), so every
     * surface that says "a read is coming" says so at once, and only the wire waits.
     */
    enabled: wantsMaterial && !isComposing && !isReadingPost,
  })
  /*
   * What the fetch has to say — which is a different question from what it holds,
   * and the reader needs both: "no material yet" and "no material, ever" are the
   * same value and opposite meanings. The author's own preview is the one case
   * with nothing to ask for: its material is the sheet's state, in hand by
   * definition, so it is never "unasked" and never "none".
   *
   * A post being read is `asking`, said here rather than reported by the hook for the reason
   * the hook cannot: no fetch has been made yet — none can be, until the post has answered —
   * but a read *is* coming, which is the whole of what the state means. It is the same
   * judgment the reader makes about its own open ("opening the reader is the ask"), one round
   * trip earlier.
   */
  const materialState: DocumentMaterialState = isReadingPost
    ? 'asking'
    : documentMaterialState({
        asked: isComposing || wantsMaterial,
        data: isComposing ? pageMaterial : materialQuery.data,
        fetchStatus: materialQuery.fetchStatus,
        isError: materialQuery.isError,
      })
  /*
   * The post's own record, re-read — the one part of the address this card cannot answer
   * from what it holds.
   *
   * `materialRef` is the post's record as the app *cached* it, so a post whose material
   * has been written again since then still names the old address here. A read started at
   * that address is answered by whatever it holds *now*, and for a document whose material
   * was answered as nothing that is an empty book: the questions are at an address this
   * card has never asked for, and no amount of asking at the old one will find them.
   * Reading the post's record again (`deriveDocumentMaterialRef`) is the only way to learn
   * what it names, and it is what stands in front of both presses below.
   *
   * Started, never awaited by the press (below): the press's own state is what it has to say
   * instantly — that a read is coming — and this round trip happens behind that.
   */
  const reReadThePost = () => {
    setIsReadingPost(true)
    /*
     * Read into a local and decided *outside* the `try`, because React Compiler will not
     * compile a component holding value blocks — a conditional, a logical, an optional chain —
     * inside a `try`/`catch`: it skipped the whole card for the first spelling of this
     * function, which is what `#/lib/reactCompilerMemoization` is for. The `try` holds the
     * await and nothing else.
     */
    const read = (async () => {
      let fresh: DocumentMaterialRef | undefined
      if (postUri) {
        try {
          fresh = await deriveDocumentMaterialRef({agent, postUri})
        } catch {
          // The post could not be re-read — offline again, or a refusal on the record
          // itself. The address in hand is the only one there is.
          fresh = undefined
        }
      }
      if (fresh) setRecovered({namedBy: materialRef?.uri, ref: fresh})
      return fresh
    })()
    void read.then(() => {
      setIsReadingPost(false)
    })
    return read
  }
  /*
   * The press that starts the read — the document's own play, the page's clip control, and
   * the reader's asks: its open, a card already up on arrival, and every door that walks
   * into a page's questions.
   *
   * It re-reads the post first and starts the read at what *that* names, so an address the post
   * has moved away from is not asked at all rather than asked at and answered as nothing — the
   * whole of the fix for the document that reads as empty. The pending state is not what waits:
   * the flag flips here, on the press, and it is the query's own ask that is held until the post
   * has answered (`enabled: … && !isReadingPost` above).
   *
   * Only a press that can start something re-reads. `asking` and `ready` are the states
   * where asking again starts nothing, and the reader's doors call this whenever a page
   * that asks something comes up — most pages, in a book that asks — so a `getRecord` in
   * front of each of those would be one per page turned. `failed` is the state the retry
   * owns, one press below. What is left is exactly the states where this card has nothing:
   * `unasked`, where no read has run, and `none`, where one ran and answered nothing. The
   * author's own composer is not asking the post for anything at all — its material is the
   * sheet's, in hand by definition — so it never re-reads.
   *
   * One identity for the card's whole life (`useNonReactiveCallback`), which is what it has
   * always had: the reader asks on its own facts rather than on this function's identity
   * (`#/lib/document/useMaterialRequest`), but a card that re-renders for a like landing
   * should not be handing a reader a new arrow to hold in its dependency lists either. The
   * wrapper reads the latest facts, so the judgment above is the state *now* rather than the
   * one this arrow was written with.
   */
  const requestMaterial = useNonReactiveCallback(() => {
    if (
      !isComposing &&
      (materialState === 'unasked' || materialState === 'none')
    ) {
      void reReadThePost()
    }
    setWantsMaterial(true)
  })
  /*
   * The same read again, asked for by hand — and re-derived rather than repeated.
   *
   * Asking again is what turns a failed fetch back into a fetch (`retry` off the query): "the
   * record is not there" was answered and cached as nothing, but a failure is cached as nothing
   * *at all*, so asking again is a real request rather than a re-read of a stored answer. What
   * the press must *not* do is ask at the address the cached post record named, because that
   * is the one part of the request a reader can do nothing about: a post whose record has
   * since been rewritten still names the old address in the cache, and a press that repeats
   * it can only fail the same way, however many times it is pressed.
   *
   * So the press reads the post's record first (`reReadThePost`, the same one the start above
   * uses) and asks at what *that* names. The address it compares against is the one this card
   * is asking at *now* (`materialRefNow`) rather than the prop: a start earlier in this
   * card's life may have recovered a moved address already, and a retry that compared against
   * the prop would read that recovery as a move it had just made, and do nothing.
   *
   * When the address has moved, holding the fresh ref is the whole request: the query is
   * keyed by the address, so the ask happens on the render that follows. When it has not —
   * the ordinary bad-minute case, and the only one the old retry got right — the request is
   * the query's own `refetch`, at an address that has just been confirmed.
   *
   * A press that cannot re-read the post (offline again, or a refusal on the record itself)
   * falls back to `refetch`: an address already in hand is worth asking, and a press that
   * did nothing at all is the one outcome a reader would not understand.
   *
   * Not wrapped: react-query hands `refetch` a new identity per render, and every reader of
   * this is a *press* rather than an effect — the card's retry pill, and the reader's own two
   * places (its failed card and the page's clip control), which keep their own copies stable.
   */
  const retryMaterial = async () => {
    const namedBy = materialRefNow?.uri
    const fresh = await reReadThePost()
    if (!fresh || fresh.uri === namedBy) await materialQuery.refetch()
  }
  const material: DocumentMaterial | undefined = isComposing
    ? pageMaterial
    : (materialQuery.data ?? undefined)
  /**
   * The composer's own page table, built from what it holds.
   *
   * It has no address because it is not *at* one yet — there is nothing to fetch
   * and nothing to point at until the post is published — which is why the two
   * halves of the material are typed apart rather than folded into one object.
   */
  const localPageTable = pageMaterial
    ? materialPagesOf(pageMaterial)
    : undefined
  /*
   * The table this card draws from: the composer's own, or the one the post names.
   *
   * A plain value rather than a `useMemo`, as are `leaderboard`, `playerProof` and
   * `resume` in `./useDocumentPlaySession`, and that is a retreat rather than an oversight.
   * React Compiler cannot *preserve* a hand-written memo here: these derivations are cheap
   * enough that it merges them into whatever consumes them, and a memo whose counterpart is
   * not in its own output is one it refuses to accept — which costs the *whole component* its
   * compilation (see `#/lib/reactCompilerMemoization`, whose record used to carry that file as
   * skipped for exactly this reason). Verified by that record: the card compiles with nothing
   * skipped, and the values below are cached by the compiler where they are read instead.
   *
   * What that costs, stated rather than implied: in the author's own carousel the object
   * is rebuilt on each render instead of being held. The one consumer the old comment
   * there named — the pager's own callback — is not affected, because `usePagerGestures`
   * reads `onPageChange` through a ref (its `stateRef`); the two surfaces that are handed
   * this table are drawn from the material itself, which changes whenever the author's
   * does anyway.
   *
   * **`react-hooks/exhaustive-deps` warns where a consumer takes this as a callback
   * dependency**, and the warning is left standing, on purpose. The rule wants this
   * conditional wrapped in a `useMemo`, which is exactly what the compiler refuses (above) —
   * and, worse, sealing the warning with an `eslint-disable` is *itself* a bailout: the
   * compiler skips any component holding a disabled React rule (`React Compiler has skipped
   * optimizing this component because one or more React ESLint rules were disabled`, measured
   * on this very line when it was the card's). So the budget is one warning in the editor
   * against a component the compiler would otherwise leave uncompiled, and that is the trade
   * taken. The identity the rule is asking about is real and is answered by the compiler: it
   * memoizes what reads this table, which is what the hand-written memo used to do by hand.
   */
  const pageTable: DocumentMaterialRef | undefined = isComposing
    ? localPageTable
      ? {uri: '', pages: localPageTable}
      : undefined
    : materialRefNow

  /*
   * The totals are summed from the page table itself, in both roles.
   *
   * The table *is* the material's summary — the composer's is built from what the
   * author has just written and the reader's off the post — so one sum serves both,
   * and the footer of a composer preview counts exactly as the published card will.
   */
  const totals = totalsOf(pageTable)
  /**
   * Where playing the document starts: its first page that asks something.
   *
   * `undefined` when no page does, which is what keeps the callout off a document
   * with nothing to play.
   */
  const firstQuizPage = firstQuestionPage(pageTable)

  return {
    /** The questions themselves, when the fetch has answered with any. */
    material,
    /** The address this card asks at *now* — a recovered one once the post has been re-read. */
    materialUri: materialRefNow?.uri,
    /** What each page holds, and the deck's totals: the post's own, or the composer's. */
    pageTable,
    totals,
    firstQuizPage,
    materialState,
    requestMaterial,
    retryMaterial,
  }
}
